import { Calendar, Check, MoreVertical } from "lucide-react";
import Button from "../common/Button";
import StatusMenu from "./StatusMenu";
import { useState } from "react";

const ApplicationRow = ({ app, navigate, onStatusUpdate }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const statusOptions = ['submitted', 'under_review', 'shortlisted', 'interview', 'rejected'];

    const getStatusInfo = (status) => {
        switch (status) {
            case 'submitted': return { text: 'Submitted', color: 'bg-blue-100 text-blue-800' };
            case 'under_review': return { text: 'Under Review', color: 'bg-yellow-100 text-yellow-800' };
            case 'shortlisted': return { text: 'Shortlisted', color: 'bg-purple-100 text-purple-800' };
            case 'interview': return { text: 'Interview', color: 'bg-indigo-100 text-indigo-800' };
            case 'offer_extended': return { text: 'Offer Extended', color: 'bg-green-100 text-green-800' };
            case 'hired': return { text: 'Hired', color: 'bg-emerald-100 text-emerald-800' };
            case 'rejected': return { text: 'Rejected', color: 'bg-red-100 text-red-800' };
            default: return { text: status, color: 'bg-gray-100 text-gray-800' };
        }
    };

    // Close menu when clicking outside
    const handleMenuToggle = (e) => {
        e.stopPropagation();
        setMenuOpen(!menuOpen);
    };

    // Close the menu
    const closeMenu = () => setMenuOpen(false);

    return (
        <div className="p-4 hover:bg-gray-50 transition-colors duration-200 relative">
            {/* Mobile Layout */}
            <div className="md:hidden space-y-3">
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm flex-shrink-0">
                            {app.applicant_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <button 
                                onClick={() => navigate(`/candidate-profile/${app.id}`)} 
                                className="font-bold text-secondary-800 text-left hover:underline block truncate"
                            >
                                {app.applicant_name || 'Unnamed Applicant'}
                            </button>
                            <p className="text-sm text-secondary-500 mt-1 truncate">
                                {app.job_title || 'No specialization provided'}
                            </p>
                        </div>
                    </div>
                    <StatusMenu app={app} onStatusUpdate={onStatusUpdate}/>
                </div>
                
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-secondary-600">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(app.created_at).toLocaleDateString()}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusInfo(app.status).color}`}>
                        {getStatusInfo(app.status).text}
                    </span>
                </div>
                
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/candidate-profile/${app.id}`)}
                    className="w-full justify-center"
                >
                    View Application
                </Button>
            </div>

            {/* Tablet Layout (md) */}
            <div className="hidden md:grid lg:hidden grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm flex-shrink-0">
                        {app.applicant_name?.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                        <button 
                            onClick={() => navigate(`/candidate-profile/${app.id}`)} 
                            className="font-bold text-secondary-800 text-left hover:underline block truncate"
                        >
                            {app.applicant_name || 'Unnamed Applicant'}
                        </button>
                        <p className="text-sm text-secondary-500 truncate">
                            {app.job_title || 'No specialization provided'}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center text-sm text-secondary-600">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(app.created_at).toLocaleDateString()}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusInfo(app.status).color}`}>
                        {getStatusInfo(app.status).text}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/candidate-profile/${app.id}`)}>
                            View
                        </Button>
                        <StatusMenu app={app} onStatusUpdate={onStatusUpdate} />
                    </div>
                </div>
            </div>

            {/* Desktop Layout (lg+) */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                <div className="col-span-5 flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg flex-shrink-0">
                        {app.applicant_name?.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                        <button 
                            onClick={() => navigate(`/candidate-profile/${app.id}`)} 
                            className="font-bold text-secondary-800 text-left hover:underline block truncate"
                        >
                            {app.applicant_name || 'Unnamed Applicant'}
                        </button>
                        <p className="text-sm text-secondary-500 truncate">
                            {app.job_title || 'No specialization provided'}
                        </p>
                    </div>
                </div>
                <div className="col-span-2 text-sm text-secondary-600">
                    {new Date(app.created_at).toLocaleDateString()}
                </div>
                <div className="col-span-3">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${getStatusInfo(app.status).color}`}>
                        {getStatusInfo(app.status).text}
                    </span>
                </div>
                <div className="col-span-2 text-right relative">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/candidate-profile/${app.id}`)}>View</Button>
                    <StatusMenu app={app} onStatusUpdate={onStatusUpdate} className={"inline-block ml-2"} />
                </div>
            </div>
            
            {/* Close menu when clicking outside */}
            {menuOpen && (
                <div 
                    className="fixed inset-0 z-10" 
                    onClick={closeMenu}
                />
            )}
        </div>
    );
};

export default ApplicationRow;